import React, {
  ChangeEvent,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import styled from 'styled-components';
import { Menubar as PrimeReactMenuBar } from 'primereact/menubar';
import ConfirmDialog from 'components/UI/ConfirmDialog';
import Text from 'components/UI/Text';
import { useStores } from 'stores/hooks/hooks';
import Input from 'components/UI/Input';
import RoleDialog from 'components/shared/RoleDialog';
import BackdropLoading from 'components/UI/BackdropLoading';
import Grid from 'components/UI/Grid/Grid';
import { saveAs } from 'file-saver';
import { MenuItem } from 'primereact/menuitem';
import { IS_APP_SYSTEMS_DISABLED, AREA_UNITS } from 'core/constants';
import { GrowerType, TreeGrower, ResellerTreeNode } from 'models/grower';
import { ActionMode, DialogCloseAction } from 'models/shared';
import {
  PlotModel,
  PlotExcel,
  PlotStatus,
  InitialStageModel,
} from 'models/plot';
import { UserGrowerRole } from 'models/user';
import api from 'Api/ApiMethods';
import { ModelType } from 'models/features';
import { PlotTableViewMode, TabViewMode } from 'core/enums';
import { ToggleBox, ToggleBoxProps } from 'components/shared/ToggleBox';
import { ServiceLevel } from 'models/restTypes';
import MoveDialog from './ResellersTable/MoveDialog/MoveDialog';
import GrowerManagementDialog from './GrowerManagementDialog/GrowerManagementDialog';
import PlotUploadDialog from './Plots/PlotUploadDialog/PlotUploadDialog';
import PlotStatusToggle from './Plots/PlotActivateToggle/PlotStatusToggle';
import PlotManagementDialog from './Plots/PlotManagementDialog/PlotManagementDialog';
import SystemsManagementDialog from './Systems/SystemsManagementDialog';
import AssignUsersDialog from './AssignUsersDialog/AssignUsersDialog';
import { ResellersGrowerTab } from './Resellers';
import FeatureManagementDialog from './Features/FeatureManagementDialog';
import PlotSettingsManagementDialog from './Plots/PlotManagementDialog/PlotSettingsManagementDialog/PlotSettingsManagementDialog';
import ResellerMassEditDialog from './Plots/ResellerMassEditPlots/ResellerMassEditDialog';
import ManualSendDialog from './ManualSendDIalog/ManualSendDialog';
import ActionManagementDialog from './Actions/ActionManagementDialog';
import PlotGBIDialog from './Plots/PlotGBIDialog/PlotGBIDialog';
import PlotDssDialog from './Plots/PlotDssDialog/PlotDssDialog';
import PlotTriggersDialog from './Plots/PlotTriggersDialog/PlotTriggersDialog';

const excelFileType =
  'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const StyledInput = styled(Input)`
  background-color: ${(p) => p.theme.palette.background.default};
`;

const StyledPrimeReactMenuBar = styled(PrimeReactMenuBar)`
  position: relative;
  z-index: 2;
  .p-submenu-list {
    width: auto;
  }

  .p-menuitem-text {
    text-wrap: nowrap;
  }
`;

const SelectedNodeTitle = styled(Text)`
  color: ${({ theme }) => theme.color.text};
  font-weight: 600;
`;

const defaultUnitValue = AREA_UNITS[0].id;

interface MenuBarEndComponentsProps {
  isPlots: boolean;
  plotViewModeProps: {
    plotViewMode: TabViewMode;
    plotsViewOptions: ToggleBoxProps['options'];
    plotViewOnChange: (
      _event: MouseEvent<HTMLElement>,
      value: TabViewMode,
    ) => void;
    plotViewModeVisible: boolean;
  };
  plotTableViewModeProps: {
    toggleViewMode: PlotTableViewMode;
    toggleViewModeOptions: ToggleBoxProps['options'];
    toggleOnChange: (
      _event: MouseEvent<HTMLElement>,
      value: PlotTableViewMode,
    ) => void;
    toggleVisible: boolean;
  };
}

const MenuBarEndComponents: FC<MenuBarEndComponentsProps> = ({
  isPlots,
  plotViewModeProps,
  plotTableViewModeProps,
}) => {
  const { resellersStore } = useStores();
  const { selectedRowsPlots } = resellersStore;
  const selectedPlot = selectedRowsPlots[0];
  const isActivateToggleVisible =
    isPlots &&
    selectedRowsPlots.length === 1 &&
    selectedPlot.status === PlotStatus.Draft;
  // const plotCoordinates = usePlotCoordinates(resellersStore.selectedRowsPlots);

  return (
    <Grid gap="1rem" direction="row">
      {/* <WeatherToolSelect
        disabledPlotsOptions={resellersStore.selectedRowsPlots.length === 0}
        coordinates={plotCoordinates}
      /> */}
      {plotTableViewModeProps.toggleVisible && (
        <ToggleBox
          value={plotTableViewModeProps.toggleViewMode}
          options={plotTableViewModeProps.toggleViewModeOptions}
          onChange={plotTableViewModeProps.toggleOnChange}
        />
      )}
      {isActivateToggleVisible && <PlotStatusToggle />}
      {plotViewModeProps.plotViewModeVisible && (
        <ToggleBox
          value={plotViewModeProps.plotViewMode}
          options={plotViewModeProps.plotsViewOptions}
          onChange={plotViewModeProps.plotViewOnChange}
        />
      )}
    </Grid>
  );
};

const Menubar: FC<{
  selectedTab: number;
  plotViewModeProps: {
    plotViewMode: TabViewMode;
    plotsViewOptions: ToggleBoxProps['options'];
    plotViewOnChange: (
      _event: MouseEvent<HTMLElement>,
      value: TabViewMode,
    ) => void;
    plotViewModeVisible: boolean;
  };
  plotTableViewModeProps: {
    toggleViewMode: PlotTableViewMode;
    toggleViewModeOptions: ToggleBoxProps['options'];
    toggleOnChange: (
      _event: MouseEvent<HTMLElement>,
      value: PlotTableViewMode,
    ) => void;
    toggleVisible: boolean;
  };
}> = ({ selectedTab, plotViewModeProps, plotTableViewModeProps }) => {
  const { t } = useTranslation('grower');
  const {
    resellersStore,
    membersStore,
    plotsStore,
    systemsStore,
    featuresStore,
    userStore,
    actionsStore,
  } = useStores();

  const isSender = useMemo(
    () => userStore?.user?.roleId === 18,
    [userStore?.user?.roleId],
  );

  const [removeResellersDialogOpen, setRemoveResellersDialogOpen] =
    useState(false);

  const [isActivateGrowerDialogVisible, setActivateGrowerDialogVisible] =
    useState<boolean>(false);

  const [moveDialog, setMoveDialog] = useState(false);
  const [definedRole, setDefinedRole] = useState<string>('');
  const [assignUsersDialogVisible, setAssignUsersDialogVisible] =
    useState<boolean>(false);

  const [editRoleDialog, setEditRoleDialog] = useState(false);
  const [unassignUsers, setUnassignUsers] = useState(false);
  const [growerManagementDialog, setGrowerManagementDialog] = useState(false);
  const [manualSendDialog, setManualSendDialog] = useState(false);
  const [growerMassEditDialog, setGrowerMassEditDialog] = useState(false);
  const [actionsDialog, setActionsDialog] = useState(false);
  const [plotManagementDialog, setPlotManagementDialog] = useState(false);
  const [plotSettingsManagementDialog, setPlotSettingsManagementDialog] =
    useState(false);

  const [plotGBIDialogVisible, setPlotGBIDialogVisible] = useState(false);
  const [plotDssDialog, setPlotDssDialog] = useState(false);
  const [plotTriggersDialog, setPlotTriggersDialog] = useState(false);

  const [removeSystemDialogVisible, setRemoveSystemDialogVisible] =
    useState<boolean>(false);

  const [removeActionDialogVisible, setRemoveActionDialogVisible] =
    useState<boolean>(false);

  const [stopPlotDialogVisible, setStopPlotDialogVisible] =
    useState<boolean>(false);

  const [systemsManagementDialogVisible, setSystemsManagementDialogVisible] =
    useState(false);

  const [mlModelManagementDialogVisible, setMLModelManagementDialogVisible] =
    useState(false);

  const [removeFeatureDialogVisible, setRemoveFeatureDialogVisible] =
    useState<boolean>(false);

  const [createTitle, setCreateTitle] = useState('');
  const [growerActionMode, setGrowerActionMode] = useState<ActionMode>(
    ActionMode.CREATE,
  );

  const [systemActionMode, setSystemActionMode] = useState<ActionMode>(
    ActionMode.CREATE,
  );

  const [mlModelActionMode, setMLModelActionMode] = useState<ActionMode>(
    ActionMode.CREATE,
  );

  const [actionsActionMode, setActionsActionMode] = useState<ActionMode>(
    ActionMode.CREATE,
  );

  const [plotFileName, setPlotFileName] = useState<string>('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [plotActionMode, setPlotActionMode] = useState<ActionMode>(
    ActionMode.CREATE,
  );

  const [isPlot, setIsPlot] = useState<boolean>(false);
  const [menuModel, setMenuModel] = useState<MenuItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { editSystemForm } = systemsStore;
  const selectedGrowerType = resellersStore?.selectedTreeNode?.data?.growerType;
  const growerId = resellersStore.selectedGrowerId;
  const isSinglePlotSelected = resellersStore.selectedRowsPlots.length === 1;
  const isDSSEnabled = useMemo(
    () =>
      resellersStore.selectedRowsPlots.every(
        (plotItem) => plotItem.serviceLevel === ServiceLevel.MONITOR,
      ),
    [resellersStore.selectedRowsPlots],
  );

  const isGBIEnabled = useMemo(
    () =>
      resellersStore.selectedRowsPlots.every(
        (plotItem) => plotItem.serviceLevel === ServiceLevel.GBI,
      ),
    [resellersStore.selectedRowsPlots],
  );

  const plotHasSystems = (resellersStore.plotSystems.data || []).length > 0;
  console.log(
    'plotHasSystems',
    JSON.parse(JSON.stringify(resellersStore.plotSystems.data)),
  );

  const actionsSelected = actionsStore.selectedActions.length > 0;

  useEffect(() => {
    membersStore.getGrowersRoles(resellersStore.resellerUsers[0]?.userId || 1);
  }, [membersStore, resellersStore]);

  const onCloseRemoveDialog = useCallback(
    (response: DialogCloseAction) => {
      if (response === DialogCloseAction.Confirmed) {
        resellersStore.removeSelectedGrowers();
      }

      setRemoveResellersDialogOpen(false);
    },
    [resellersStore],
  );

  const onCloseActivateDialog = useCallback(
    (response: DialogCloseAction) => {
      if (
        response === DialogCloseAction.Confirmed &&
        resellersStore.selectedGrowersForActivation.length
      ) {
        const newStatus =
          !resellersStore.selectedGrowersForActivation[0]?.isActive;

        resellersStore
          .updateGrowersStatus(
            resellersStore.selectedGrowersForActivation,
            newStatus,
          )
          .then(() => {
            setActivateGrowerDialogVisible(false);
          });
      } else {
        setActivateGrowerDialogVisible(false);
      }
    },
    [resellersStore],
  );

  const onCloseUnassignDialog = useCallback(
    (response: DialogCloseAction) => {
      if (response === DialogCloseAction.Confirmed) {
        resellersStore.unassignUsersFromGrower(
          growerId,
          resellersStore.selectedRowsUsers,
        );
      }

      setUnassignUsers(false);
    },
    [resellersStore, growerId],
  );

  const handleMoveDialogClose = useCallback(() => {
    setMoveDialog(false);
    resellersStore.setSelectedRows({ selection: [], activeRow: null });
  }, [resellersStore]);

  const onEditDialogRole = useCallback(() => {
    const name =
      resellersStore.selectedRowsUsers.length === 1
        ? resellersStore.selectedRowsUsers[0]?.userRole
        : '';

    setDefinedRole(name || '');
    setEditRoleDialog(true);
  }, [resellersStore]);

  const onCloseEditRoleDialog = useCallback(
    (response: DialogCloseAction, selectedRole: UserGrowerRole | null) => {
      if (response === DialogCloseAction.Confirmed && selectedRole) {
        resellersStore.editResellerUserRole(selectedRole);
      }

      setEditRoleDialog(false);
    },
    [resellersStore],
  );

  const onMassEditDialog = useCallback(() => {
    setGrowerMassEditDialog(true);
  }, []);

  const onActionsDialog = useCallback(() => {
    setActionsActionMode(ActionMode.CREATE);
    setActionsDialog(true);
  }, []);

  const handleActionsDialogEdit = useCallback(() => {
    setActionsActionMode(ActionMode.EDIT);
    setActionsDialog(true);
  }, []);

  const handleActionsDialogClose = useCallback(() => {
    setActionsDialog(false);
  }, []);

  const handleGrowerMassEditDialogClose = useCallback(() => {
    setGrowerMassEditDialog(false);
  }, []);

  const onCreate = useCallback(
    (growerType: GrowerType) => {
      resellersStore.setSelectedRows({ selection: [], activeRow: null });
      resellersStore.growerAction.name = '';
      resellersStore.growerAction.growerType = growerType;
      resellersStore.growerAction.resellerId = growerId || 0;

      setGrowerActionMode(ActionMode.CREATE);
      setCreateTitle(
        growerType === GrowerType.Reseller
          ? t('create_reseller')
          : t('create_grower'),
      );

      setGrowerManagementDialog(true);
    },
    [t, growerId, resellersStore],
  );

  const onEdit = useCallback(() => {
    resellersStore.growerAction =
      resellersStore.selectedRows[0] || ({} as ResellerTreeNode);

    setGrowerActionMode(ActionMode.EDIT);
    setCreateTitle(
      t('edit_entity', {
        type:
          resellersStore.growerAction.growerType === GrowerType.Reseller
            ? 'Reseller'
            : 'Grower',
      }),
    );

    setGrowerManagementDialog(true);
  }, [t, resellersStore]);

  const handleGrowerManagementDialogClose = useCallback(() => {
    setGrowerManagementDialog(false);
    if (growerActionMode === ActionMode.EDIT) {
      const grower = resellersStore.tableValues.find(
        (g) => g?.id === resellersStore.growerAction.id,
      );

      resellersStore.setSelectedRows({
        selection: [],
        activeRow: (grower as TreeGrower) ?? null,
      });
    }
  }, [growerActionMode, resellersStore]);

  const downloadPlotTemplate = useCallback(async () => {
    const file = await api.getPlotTemplate();

    saveAs(
      new Blob([file], { type: 'application/octet-stream' }),
      'plot_template.xlsx',
    );
  }, []);

  const getSelectedPlotFields = useCallback((): Partial<PlotModel> => {
    const plot = resellersStore.selectedRowsPlots[0];

    return {
      growerId,
      plotId: plot.id,
      id: plot.id,
      area: {
        unit: plot.area?.unit,
        val: plot.area?.val,
      },
      initialStage:
        {
          date: plot.initialStage?.date,
          stageId: plot.initialStage?.stageId,
        } || {},
      crop: plot.crop,
      cropProtocolId: plot.cropProtocolId,
      previousProtocolId: plot.previousProtocolId,
      name: plot.name,
      description: plot.description,
      growingType: plot.growingType,
      growthMethod: plot.growthMethod,
      irrigationMethod: plot.irrigationMethod,
      latitude: plot.latitude,
      longitude: plot.longitude,
      plantTime: plot.plantTime,
      startTime: plot.startTime,
      sampleFrequency: plot.sampleFrequency || 'NORMAL',
      serviceLevel: plot.serviceLevel,
      soil: plot.soil,
      seasonTime: plot.startTime,
      variety: plot.variety,
      category: plot.category,
      geojson: plot.geojson,
      profileId: plot.profileId,
      geocode: plot.geocode,
      timeZone: plot?.timeZone,
    } as Partial<PlotModel>;
  }, [resellersStore.selectedRowsPlots, growerId]);

  const getDefaultSelectData = useCallback(() => {
    plotsStore.getPlotProfile();
    plotsStore.getGrowingType();
    plotsStore.getIrrigationMethods();
    plotsStore.getServiceLevel();
    plotsStore.getSoilType();
  }, [plotsStore]);

  const getCropSelectData = useCallback(() => {
    plotsStore.getCropTypes(growerId);
    plotsStore.getPenologicalStages();
    plotsStore.getGrowingMethod();
    plotsStore.getCategories(
      plotsStore.plotAction.growerId,
      plotsStore.plotAction.crop,
    );

    plotsStore.getVarieties(
      plotsStore.plotAction.growerId,
      plotsStore.plotAction.crop,
    );
  }, [plotsStore, growerId]);

  const getPlotParams = useCallback(() => {
    const plotId = resellersStore.selectedRowsPlots[0];
    plotsStore.getPlotParams(plotId.id);
  }, [plotsStore, resellersStore.selectedRowsPlots]);

  const openPlotCreateModal = useCallback(() => {
    setPlotActionMode(ActionMode.CREATE);
    getDefaultSelectData();
    const defaultPlot = {
      growerId,
      area: {
        unit: defaultUnitValue,
      },
      initialStage: {},
      variety: '',
      sampleFrequency: 'NORMAL',
      startTime: new Date().getTime(),
      status: PlotStatus.Draft,
    };

    plotsStore.setPlotAction(defaultPlot as PlotModel);
    setPlotManagementDialog(true);
  }, [plotsStore, growerId, getDefaultSelectData]);

  const openPlotEditModal = useCallback(() => {
    setPlotActionMode(ActionMode.EDIT);
    getDefaultSelectData();

    const plotToEdit = getSelectedPlotFields();

    plotsStore.setPlotAction(plotToEdit as PlotModel);
    getCropSelectData();

    if (plotsStore.plotAction.name) {
      setPlotManagementDialog(true);
    }
  }, [
    plotsStore,
    getSelectedPlotFields,
    getDefaultSelectData,
    getCropSelectData,
  ]);

  const openPlotSettingsModal = useCallback(() => {
    setPlotActionMode(ActionMode.EDIT);
    getDefaultSelectData();

    const plotToEdit = getSelectedPlotFields();

    plotsStore.setPlotAction(plotToEdit as PlotModel);
    getPlotParams();

    if (plotsStore.plotAction.name) {
      setPlotSettingsManagementDialog(true);
    }
  }, [getDefaultSelectData, getSelectedPlotFields, plotsStore, getPlotParams]);

  const openPlotDuplicateModal = useCallback(() => {
    setPlotActionMode(ActionMode.CREATE);
    getDefaultSelectData();

    const selectedPlot = getSelectedPlotFields();
    const duplicatePlotData: Partial<PlotModel> = {
      ...selectedPlot,
      name: `${selectedPlot.name} copy`,
      geojson: null,
      area: { ...selectedPlot.area, val: 0 },
      initialStage: {} as InitialStageModel,
      startSeason: undefined,
    };

    plotsStore.setPlotAction(duplicatePlotData as PlotModel);
    plotsStore.plotManagementValidation.location = true;
    getCropSelectData();

    if (plotsStore.plotAction.name) {
      setPlotManagementDialog(true);
    }
  }, [
    plotsStore,
    getSelectedPlotFields,
    getDefaultSelectData,
    getCropSelectData,
  ]);

  const handlePlotManagementDialogClose = useCallback(() => {
    setPlotManagementDialog(false);
    plotsStore.resetStore();
  }, [plotsStore]);

  const handlePlotSettingsManagementDialogClose = useCallback(() => {
    setPlotSettingsManagementDialog(false);
    plotsStore.resetStore();
  }, [plotsStore]);

  const openPlotGBIModal = useCallback(() => {
    setPlotGBIDialogVisible(true);
  }, []);

  const handlePlotGBIDialogClose = useCallback(() => {
    setPlotGBIDialogVisible(false);
  }, []);

  const handlePlotDssDialogClose = useCallback(() => {
    setPlotDssDialog(false);
  }, []);

  const handlePlotTriggersDialogClose = useCallback(() => {
    setPlotTriggersDialog(false);
  }, []);

  const handleSystemsManagementDialogClose = useCallback(() => {
    setSystemsManagementDialogVisible(false);
    systemsStore.resetStore();
  }, [systemsStore]);

  const handleMLModelManagementDialogClose = useCallback(() => {
    featuresStore.setSelectedSensors([]);
    featuresStore.setFeatureType({} as ModelType);
    setMLModelManagementDialogVisible(false);
  }, [featuresStore]);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target?.files?.[0]) {
        const fileName = e.target?.files?.[0]?.name;
        const res: PlotExcel[] = await resellersStore.uploadExcelPlots(
          e.target.files[0],
        );

        if (res && fileName) {
          setPlotFileName(fileName);
        }
      }

      e.target.value = '';
    },
    [resellersStore],
  );

  const handlePlotDialogClose = useCallback(() => {
    setPlotFileName('');
  }, []);

  const showAssignUsersDialog = useCallback(() => {
    setAssignUsersDialogVisible(true);
  }, []);

  const hideAssignUsersDialog = useCallback(() => {
    setAssignUsersDialogVisible(false);
  }, []);

  const handleSystemDialogOpen = useCallback((mode: ActionMode) => {
    setSystemsManagementDialogVisible(true);
    setSystemActionMode(mode);
  }, []);

  const handleMlModelDialogOpen = useCallback((mode: ActionMode) => {
    setMLModelManagementDialogVisible(true);
    setMLModelActionMode(mode);
  }, []);

  const handleManualSendDialogOpen = useCallback(() => {
    setManualSendDialog(true);
  }, []);

  const handleManualSendDialogClose = useCallback(() => {
    setManualSendDialog(false);
  }, []);

  const handleRemoveSystemSubmit = useCallback(
    (value: DialogCloseAction) => {
      if (value !== DialogCloseAction.Confirmed) {
        setRemoveSystemDialogVisible(false);
        return;
      }

      const systemId = resellersStore.selectedRowsSystems[0]?.id;
      if (systemId) {
        resellersStore.removeGrowerSystem(systemId).then(() => {
          setRemoveSystemDialogVisible(false);
        });
      }
    },
    [resellersStore],
  );

  const handleRemoveActionSubmit = useCallback(
    (value: DialogCloseAction) => {
      if (value !== DialogCloseAction.Confirmed) {
        setRemoveActionDialogVisible(false);
        return;
      }

      const actionId = actionsStore.selectedActions[0]?.id;
      const systemId = actionsStore.selectedActions[0]?.systemId;
      if (actionId && systemId) {
        actionsStore.removeAction(systemId, actionId).then(() => {
          setRemoveActionDialogVisible(false);
        });
      }
    },
    [actionsStore],
  );

  const handleStopPlotSubmit = useCallback(
    (value: DialogCloseAction) => {
      if (value !== DialogCloseAction.Confirmed) {
        setStopPlotDialogVisible(false);
        return;
      }

      const plotId = resellersStore.selectedRowsPlots[0]?.id;
      if (plotId) {
        resellersStore.removeResellerPlot(growerId, plotId).then(() => {
          setStopPlotDialogVisible(false);
        });
      }
    },
    [growerId, resellersStore],
  );

  const handleRemoveFeatureSubmit = useCallback(
    (value: DialogCloseAction) => {
      if (value !== DialogCloseAction.Confirmed) {
        setRemoveFeatureDialogVisible(false);
        return;
      }

      const features = featuresStore.selectedFeatures;
      if (features) {
        featuresStore.deactivateFeature().then(() => {
          setRemoveFeatureDialogVisible(false);
        });
      }
    },
    [featuresStore],
  );

  const activateMenuOptions: MenuItem[] = useMemo(() => {
    const selectedGrower = resellersStore.selectedGrowersForActivation[0];
    const isGrowerActive = selectedGrower?.isActive;
    const isGrowerHasActiveEntities = selectedGrower?.growers?.some(
      (grower) => grower.isActive,
    );

    const isActivationEnabled =
      resellersStore.selectedGrowersForActivation.length &&
      (!isGrowerHasActiveEntities || !selectedGrower.isActive);

    const isTableGrowerRowSelected =
      selectedGrowerType === GrowerType.Reseller &&
      selectedTab === ResellersGrowerTab.Growers &&
      !!resellersStore.selectedRows.length;

    const isActionVisible =
      selectedTab === ResellersGrowerTab.Details || isTableGrowerRowSelected;

    return [
      {
        label: isGrowerActive ? t('deactivate') : t('activate'),
        icon: 'fa fa-hand-paper',
        disabled: !isActivationEnabled,
        visible: isActionVisible,
        command: () => {
          setActivateGrowerDialogVisible(true);
        },
      },
    ];
  }, [
    t,
    resellersStore.selectedRows.length,
    resellersStore.selectedGrowersForActivation,
    selectedGrowerType,
    selectedTab,
  ]);

  const resellerItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('create_reseller'),
        icon: 'pi pi-fw pi-plus',
        command: () => {
          onCreate(GrowerType.Reseller);
        },
      },
      {
        label: t('create_grower'),
        icon: 'pi pi-fw pi-plus',
        command: () => {
          onCreate(GrowerType.Grower);
        },
      },
      {
        label: t('edit'),
        icon: 'pi pi-fw pi-pencil',
        disabled: !resellersStore.growerTableMultiSelection.activeRow,
        command: onEdit,
      },
      {
        label: t('remove'),
        disabled:
          !resellersStore.selectedRows.length ||
          resellersStore.isEntityHaveChildren,
        command: () => {
          setRemoveResellersDialogOpen(true);
        },
        icon: 'pi pi-fw pi-trash',
      },
      {
        label: t('manual_send'),
        disabled: !isSender,
        command: () => {
          handleManualSendDialogOpen();
        },
        icon: 'pi pi-fw pi-megaphone',
      },
      {
        label: t('move'),
        disabled: false,
        visible: true,
        icon: 'pi pi-fw pi-arrows-alt',
        command: () => {
          setMoveDialog(true);
        },
      },
      ...activateMenuOptions,
    ],
    [
      activateMenuOptions,
      onCreate,
      onEdit,
      handleManualSendDialogOpen,
      resellersStore.growerTableMultiSelection.activeRow,
      resellersStore.isEntityHaveChildren,
      resellersStore.selectedRows,
      isSender,
      t,
    ],
  );

  const itemsUsers: MenuItem[] = useMemo(
    () => [
      {
        label: t('assign_users'),
        command: showAssignUsersDialog,
      },
      {
        label: t('edit_role'),
        disabled: !resellersStore.selectedRowsUsers.length,
        icon: 'pi pi-fw pi-pencil',
        command: onEditDialogRole,
      },
      {
        label: t('unassign'),
        disabled: !resellersStore.selectedRowsUsers.length,
        command: () => {
          setUnassignUsers(true);
        },
      },
    ],
    [
      onEditDialogRole,
      showAssignUsersDialog,
      resellersStore.selectedRowsUsers.length,
      t,
    ],
  );

  const actionsItems: MenuItem[] = useMemo(() => {
    return [
      {
        label: t('actions_add'),
        icon: 'pi pi-fw pi-plus',
        command: onActionsDialog,
      },
      {
        label: t('actions_edit'),
        icon: 'pi pi-fw pi-pencil',
        disabled: !actionsSelected,
        command: handleActionsDialogEdit,
      },
      {
        label: t('remove_entity', { type: 'Action' }),
        icon: 'pi pi-fw pi-trash',
        command: () => setRemoveActionDialogVisible(true),
        disabled: !actionsSelected,
      },
    ];
  }, [onActionsDialog, t, actionsSelected, handleActionsDialogEdit]);

  const PlotItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        label: t('plots'),
        icon: 'pi pi-fw pi-file',
        items: [
          {
            label: t('new_plot'),
            icon: 'pi pi-fw pi-plus',
            command: openPlotCreateModal,
          },
          {
            label: t('import_plots'),
            icon: 'pi pi-fw pi-upload',
            command: () => {
              inputRef?.current?.click();
            },
          },
          {
            label: t('download_template'),
            icon: 'pi pi-fw pi-download',
            command: downloadPlotTemplate,
          },
          {
            label: t('duplicate_plot'),
            icon: 'pi pi-fw pi-copy',
            command: openPlotDuplicateModal,
            disabled: !isSinglePlotSelected,
          },
        ],
      },
    ];

    if (plotTableViewModeProps.toggleViewMode === PlotTableViewMode.Metadata) {
      items.push({
        label: t('edit'),
        icon: 'pi pi-fw pi-pencil',
        items: [
          {
            label: t('edit_plot'),
            icon: 'pi pi-fw pi-pencil',
            command: openPlotEditModal,
            disabled: !isSinglePlotSelected,
          },
          {
            label: t('plot_settings'),
            icon: 'pi pi-fw pi-cog',
            command: openPlotSettingsModal,
            disabled: !isSinglePlotSelected,
          },
          {
            label: t('gbi_settings'),
            icon: 'pi pi-fw pi-cog',
            command: openPlotGBIModal,
            disabled: !isSinglePlotSelected || !isGBIEnabled,
          },
          {
            label: t('dss_settings'),
            icon: 'pi pi-fw pi-sliders-h',
            command: () => setPlotDssDialog(true),
            disabled: !isSinglePlotSelected || !isDSSEnabled,
          },
          {
            label: t('triggers_settings'),
            icon: 'pi pi-fw pi-bolt',
            command: () => setPlotTriggersDialog(true),
            disabled: !isSinglePlotSelected,
          },
        ],
      });

      items.push({
        label: t('remove_plot'),
        icon: 'pi pi-fw pi-trash',
        command: () => setStopPlotDialogVisible(true),
        disabled: !isSinglePlotSelected || resellersStore.plotSystems.loading,
      });
    }

    if (
      plotTableViewModeProps.toggleViewMode === PlotTableViewMode.MassEditing
    ) {
      items.push({
        label: t('mass_edit'),
        disabled: !plotsStore.selectedMassEditRows.length,
        icon: 'pi pi-fw pi-pencil',
        command: onMassEditDialog,
      });
    }

    return items;
  }, [
    t,
    openPlotCreateModal,
    downloadPlotTemplate,
    openPlotDuplicateModal,
    isSinglePlotSelected,
    plotTableViewModeProps.toggleViewMode,
    openPlotEditModal,
    openPlotSettingsModal,
    openPlotGBIModal,
    plotsStore.selectedMassEditRows.length,
    onMassEditDialog,
    resellersStore.plotSystems.loading,
    isGBIEnabled,
    isDSSEnabled,
  ]);

  const isSystemSelected = useMemo(
    () =>
      editSystemForm.selectedSystem &&
      editSystemForm.selectedSystem?.id ===
      resellersStore.selectedRowsSystems[0]?.id,
    [editSystemForm.selectedSystem, resellersStore.selectedRowsSystems],
  );

  const systemItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('add_entity', { type: 'System' }),
        icon: 'pi pi-fw pi-plus',
        command: () => handleSystemDialogOpen(ActionMode.CREATE),
        disabled:
          IS_APP_SYSTEMS_DISABLED || selectedTab !== ResellersGrowerTab.Systems,
      },
      {
        label: t('edit_entity', { type: 'System' }),
        icon: 'pi pi-fw pi-pencil',
        command: () => handleSystemDialogOpen(ActionMode.EDIT),
        disabled: IS_APP_SYSTEMS_DISABLED || !isSystemSelected,
      },
      {
        label: t('remove_entity', { type: 'System' }),
        icon: 'pi pi-fw pi-trash',
        command: () => setRemoveSystemDialogVisible(true),
        disabled: IS_APP_SYSTEMS_DISABLED || !isSystemSelected,
      },
    ],
    [t, isSystemSelected, selectedTab, handleSystemDialogOpen],
  );

  const featuresItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('add_entity', { type: 'Feature' }),
        icon: 'pi pi-fw pi-plus',
        command: () => handleMlModelDialogOpen(ActionMode.CREATE),
        disabled: selectedTab !== ResellersGrowerTab.Features,
      },
      {
        label: t('edit_entity', { type: 'Feature' }),
        icon: 'pi pi-fw pi-pencil',
        command: () => handleSystemDialogOpen(ActionMode.EDIT),
        disabled: true,
      },
      {
        label: t('remove_entity', { type: 'Feature' }),
        icon: 'pi pi-fw pi-trash',
        command: () => setRemoveFeatureDialogVisible(true),
        disabled:
          selectedTab !== ResellersGrowerTab.Features ||
          featuresStore.selectedFeatures.length === 0,
      },
    ],
    [
      t,
      selectedTab,
      featuresStore.selectedFeatures.length,
      handleMlModelDialogOpen,
      handleSystemDialogOpen,
    ],
  );

  useEffect(() => {
    setIsPlot(false);
    if (!resellersStore.selectedTreeNode) {
      setMenuModel([]);
    }

    switch (selectedTab) {
      case ResellersGrowerTab.Users: {
        setMenuModel(itemsUsers);
        break;
      }

      case ResellersGrowerTab.Plots: {
        setMenuModel(PlotItems);
        setIsPlot(true);
        break;
      }

      case ResellersGrowerTab.Systems: {
        setMenuModel(systemItems);
        break;
      }

      case ResellersGrowerTab.Features: {
        setMenuModel(featuresItems);
        break;
      }

      case ResellersGrowerTab.Growers: {
        if (selectedGrowerType === GrowerType.Reseller) {
          setMenuModel(resellerItems);
        }

        break;
      }

      case ResellersGrowerTab.Details: {
        setMenuModel(activateMenuOptions);
        break;
      }

      case ResellersGrowerTab.Actions: {
        setMenuModel(actionsItems);
        break;
      }

      default:
        setMenuModel([]);
    }
  }, [
    itemsUsers,
    PlotItems,
    systemItems,
    resellerItems,
    selectedTab,
    selectedGrowerType,
    activateMenuOptions,
    resellersStore.selectedRows,
    resellersStore.selectedTreeNode,
    featuresItems,
    actionsItems,
  ]);

  const selectedGrowerForActivation =
    resellersStore.selectedGrowersForActivation[0];

  return (
    <div>
      {removeResellersDialogOpen && (
        <ConfirmDialog
          backText={t('cancel')}
          buttonText={t('remove')}
          isOpen={removeResellersDialogOpen}
          title={t('confirm_dialog_reseller_delete_title')}
          message={t('confirm_dialog_reseller_delete_message')}
          onClose={onCloseRemoveDialog}
        />
      )}
      {isActivateGrowerDialogVisible && (
        <ConfirmDialog
          onClose={onCloseActivateDialog}
          loading={resellersStore.isUpdatingGrowerStatus}
          disabled={resellersStore.isUpdatingGrowerStatus}
          backText={t('cancel')}
          buttonText={
            selectedGrowerForActivation?.isActive
              ? t('deactivate')
              : t('activate')
          }
          isOpen={isActivateGrowerDialogVisible}
          title={i18next.t('dialog:confirm_dialog_title', {
            action: selectedGrowerForActivation?.isActive
              ? 'Deactivate'
              : 'Activate',
          })}
          message={i18next.t('dialog:confirm_dialog_action_message', {
            action: selectedGrowerForActivation?.isActive
              ? 'deactivate'
              : 'activate',
            entity:
              selectedGrowerForActivation?.growerType === GrowerType.Grower
                ? 'growers'
                : 'resellers',
          })}
        />
      )}
      {unassignUsers && (
        <ConfirmDialog
          backText={t('cancel')}
          buttonText={t('unassign')}
          isOpen={unassignUsers}
          title={t('confirm_dialog_reseller_unassign_title')}
          message={t('confirm_dialog_reseller_unassign_message')}
          onClose={onCloseUnassignDialog}
        />
      )}
      {moveDialog && (
        <MoveDialog
          onHide={handleMoveDialogClose}
          visible={moveDialog}
          title={`${t('move')} ${t('to')}`}
        />
      )}
      {editRoleDialog && (
        <RoleDialog
          backText={t('cancel')}
          buttonText={t('edit')}
          isOpen={editRoleDialog}
          title={t('users:edit_role')}
          message={t('users:supplant_role')}
          onClose={onCloseEditRoleDialog}
          definedRole={definedRole}
        />
      )}
      {growerManagementDialog && (
        <GrowerManagementDialog
          onHide={handleGrowerManagementDialogClose}
          visible={growerManagementDialog}
          title={
            growerActionMode === ActionMode.CREATE
              ? t('actions_add')
              : t('actions_edit')
          }
          mode={growerActionMode}
        />
      )}

      <BackdropLoading open={uploadLoading} />
      <input
        style={{ display: 'none' }}
        ref={inputRef}
        type="file"
        onChange={handleFileChange}
        accept={excelFileType}
      />
      <PlotUploadDialog
        fileName={plotFileName}
        onClose={handlePlotDialogClose}
      />
      {plotManagementDialog && (
        <PlotManagementDialog
          onHide={handlePlotManagementDialogClose}
          visible={plotManagementDialog}
          title={
            plotActionMode === ActionMode.CREATE
              ? `${t('create')} ${t('plot')}`
              : `${t('edit')} ${t('plot')}`
          }
          mode={plotActionMode}
        />
      )}
      {plotSettingsManagementDialog && (
        <PlotSettingsManagementDialog
          onHide={handlePlotSettingsManagementDialogClose}
          visible={plotSettingsManagementDialog}
          title={t('settings')}
          mode={plotActionMode}
        />
      )}
      {plotGBIDialogVisible && (
        <PlotGBIDialog
          onHide={handlePlotGBIDialogClose}
          visible={plotGBIDialogVisible}
          title={t('gbi_settings')}
          mode={plotActionMode}
        />
      )}

      {plotDssDialog && (
        <PlotDssDialog
          onHide={handlePlotDssDialogClose}
          visible={plotDssDialog}
          title={t('dss_settings')}
          mode={plotActionMode}
        />
      )}
      {plotTriggersDialog && (
        <PlotTriggersDialog
          onHide={handlePlotTriggersDialogClose}
          visible={plotTriggersDialog}
          title={t('triggers_settings')}
          mode={plotActionMode}
        />
      )}
      {growerMassEditDialog && (
        <ResellerMassEditDialog
          onHide={handleGrowerMassEditDialogClose}
          visible={growerMassEditDialog}
          title={t('mass_edit')}
          mode={plotActionMode}
        />
      )}
      {actionsDialog && (
        <ActionManagementDialog
          onHide={handleActionsDialogClose}
          visible={actionsDialog}
          title={t('actions')}
          mode={actionsActionMode}
        />
      )}
      {systemsManagementDialogVisible && (
        <SystemsManagementDialog
          onHide={handleSystemsManagementDialogClose}
          visible={systemsManagementDialogVisible}
          title={
            systemActionMode === ActionMode.CREATE
              ? `${t('add')} ${t('system')}`
              : `${t('edit')} ${t('system')}`
          }
          mode={systemActionMode}
        />
      )}
      {mlModelManagementDialogVisible && (
        <FeatureManagementDialog
          onHide={handleMLModelManagementDialogClose}
          visible={mlModelManagementDialogVisible}
          title={
            mlModelActionMode === ActionMode.CREATE
              ? `${t('add')} ${t('ai_feature')}`
              : `${t('edit')} ${t('ai_feature')}`
          }
          mode={systemActionMode}
        />
      )}
      {manualSendDialog && (
        <ManualSendDialog
          onHide={handleManualSendDialogClose}
          visible={manualSendDialog}
          title={t('manual_send')}
        />
      )}
      <ConfirmDialog
        backText={t('cancel')}
        buttonText={t('remove')}
        isOpen={removeSystemDialogVisible}
        title={t('remove_system_dialog_title')}
        message={t('remove_system_dialog_message')}
        loading={resellersStore.isRemovingGrowerSystem}
        disabled={resellersStore.isRemovingGrowerSystem}
        onClose={handleRemoveSystemSubmit}
      />
      <ConfirmDialog
        backText={t('cancel')}
        buttonText={t('remove')}
        isOpen={stopPlotDialogVisible}
        title={t('remove_plot_dialog_title')}
        message={
          plotHasSystems
            ? t('remove_plot_has_systems_message', {
              systems: resellersStore.plotSystems.data
                .map((system) => system?.displayName || '')
                .join('\n'),
            })
            : t('remove_plot_dialog_message')
        }
        loading={resellersStore.isRemovingResellerPlot}
        disabled={resellersStore.isRemovingResellerPlot || plotHasSystems}
        onClose={handleStopPlotSubmit}
      />
      <ConfirmDialog
        backText={t('cancel')}
        buttonText={t('remove')}
        isOpen={removeActionDialogVisible}
        title={t('remove_action_dialog_title')}
        message={t('remove_action_dialog_message')}
        loading={actionsStore.isRemovingAction}
        disabled={actionsStore.isRemovingAction}
        onClose={handleRemoveActionSubmit}
      />
      <ConfirmDialog
        backText={t('cancel')}
        buttonText={t('remove')}
        isOpen={removeFeatureDialogVisible}
        title={t('remove_feature_dialog_title')}
        message={t('remove_feature_dialog_message')}
        onClose={handleRemoveFeatureSubmit}
      />
      {assignUsersDialogVisible && (
        <AssignUsersDialog
          onHide={hideAssignUsersDialog}
          visible={assignUsersDialogVisible}
          title={t('assign_users')}
        />
      )}
      <StyledPrimeReactMenuBar
        model={menuModel}
        end={
          <MenuBarEndComponents
            isPlots={isPlot}
            plotViewModeProps={plotViewModeProps}
            plotTableViewModeProps={plotTableViewModeProps}
          />
        }
      />
    </div>
  );
};

export default observer(Menubar);
